import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ShieldCheck, Search, Globe2, LogIn, UserPlus } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import { useUserAuth } from '../context/UserAuthContext';
import { CartButton, UserButton } from './HeaderExtras';
import { cn } from '../lib/utils';
import { staticImg } from '../utils/media';

export default function Header() {
  const { meta, lang, setLang, tr } = useI18n();
  const { user } = useUserAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const normUrl = (u) => (u && u.startsWith('/') ? u : `/${u || ''}`);

  const trMenu = (title, url) => {
    if (url === '/') return tr('home.nav_home', title);
    if (url === '/products') return tr('home.nav_products', title);
    if (url === '/updates') return tr('home.nav_updates', title);
    if (url === '/blog') return tr('home.nav_blog', title);
    if (url === '/contact') return tr('home.nav_contact', title);
    return title;
  };

  // Build tree from meta.menus
  const menus = meta?.menus || [];
  const roots = menus.filter((m) => !m.parent_id).sort((a, b) => a.sort_order - b.sort_order);
  const childrenOf = (id) => menus.filter((m) => m.parent_id === id).sort((a, b) => a.sort_order - b.sort_order);
  const settings = meta?.settings || {};
  const rawLangs = meta?.languages || meta?.available || [{ code: 'tr' }, { code: 'en' }];
  const languagesList = rawLangs.map((l) => (typeof l === 'string' ? { code: l, name: l.toUpperCase() } : l));

  return (
    <header
      className={cn(
        'fixed top-0 inset-x-0 z-50 transition-all duration-500',
        scrolled
          ? 'bg-[#0a0a0f]/85 backdrop-blur-xl border-b border-[#e4b44e]/15'
          : 'bg-gradient-to-b from-black/60 to-transparent'
      )}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            {settings.site_logo ? (
              <img
                src={staticImg(settings.site_logo)}
                alt="Logo"
                onError={(e) => {
                  if (!e.target.dataset.triedAssets) {
                    e.target.dataset.triedAssets = '1';
                    e.target.src = `/assets/img/${settings.site_logo}`;
                  } else if (!e.target.dataset.triedUploads) {
                    e.target.dataset.triedUploads = '1';
                    e.target.src = `/uploads/${settings.site_logo}`;
                  }
                }}
                className="w-10 h-10 object-contain"
              />
            ) : (
              <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-[#f0c766] to-[#a97a1f] flex items-center justify-center shadow-[0_0_24px_-4px_rgba(228,180,78,0.6)]">
                <ShieldCheck className="w-5 h-5 text-[#17140b]" strokeWidth={2.4} />
                <div className="absolute inset-0 rounded-full ring-1 ring-[#e4b44e]/60 group-hover:ring-[#f0d38a]/80 transition" />
              </div>
            )}
            <div className="leading-tight">
              <div className="font-display text-base md:text-lg font-extrabold tracking-widest text-white">
                AEGIS<span className="gold-text-animated"> STUDIOS</span>
              </div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-0">
            {roots.map((item) => {
              const kids = childrenOf(item.id);
              return (
                <div key={item.id} className="relative group">
                  <Link to={normUrl(item.url)}
                    className="flex items-center gap-1 px-3 py-2 text-[12px] font-semibold tracking-wider text-[#e7dfc8]/85 hover:text-[#f4d896] transition uppercase whitespace-nowrap">
                    {trMenu(item.title, normUrl(item.url))}
                    {kids.length > 0 && <ChevronDown className="w-3.5 h-3.5 opacity-70 group-hover:rotate-180 transition" />}
                  </Link>
                  {kids.length > 0 && (
                    <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 -translate-y-1 group-hover:translate-y-0 z-50">
                      <div className="min-w-[220px] rounded-xl bg-[#0d0d13]/95 backdrop-blur-xl border border-[#e4b44e]/20 p-2 shadow-2xl shadow-black/60">
                        {kids.map((c) => (
                          <Link key={c.id} to={normUrl(c.url)}
                            className="block px-3 py-2 text-sm text-[#e7dfc8]/80 hover:text-[#f4d896] hover:bg-[#e4b44e]/10 rounded-lg transition">
                            {trMenu(c.title, normUrl(c.url))}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <CartButton />
            <button className="hidden md:flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-white/70 hover:text-[#f4d896] hover:border-[#e4b44e]/40 transition">
              <Search className="w-4 h-4" />
            </button>
            <div className="relative hidden md:block">
              <button onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 h-10 px-3 rounded-full border border-white/10 text-xs font-semibold tracking-wider text-white/80 hover:text-[#f4d896] hover:border-[#e4b44e]/40 transition">
                <Globe2 className="w-4 h-4" /> {(lang || 'tr').toUpperCase()}
                <ChevronDown className="w-3 h-3" />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 grid grid-cols-3 gap-1 min-w-[260px] rounded-xl bg-[#0d0d13]/95 backdrop-blur-xl border border-[#e4b44e]/20 p-2 shadow-2xl z-50">
                  {languagesList.map((l) => (
                    <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={cn(
                        'px-2 py-1.5 text-[11px] font-semibold rounded uppercase transition',
                        lang === l.code ? 'bg-[#e4b44e]/20 text-[#f4d896]' : 'text-white/70 hover:text-[#f4d896] hover:bg-[#e4b44e]/10',
                      )}>
                      {l.code}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!user && (
              <>
                <Link to="/login" className="hidden lg:flex items-center gap-1.5 h-10 px-4 text-xs font-bold tracking-wider text-[#e7dfc8]/85 hover:text-[#f4d896] transition whitespace-nowrap">
                  <LogIn className="w-4 h-4" /> {tr('home.login', 'GİRİŞ YAP').toUpperCase()}
                </Link>
                <Link to="/register" className="hidden lg:flex btn-gold items-center gap-1.5 h-10 px-4 rounded-full text-xs font-extrabold tracking-wider whitespace-nowrap">
                  <UserPlus className="w-4 h-4" /> {tr('home.register', 'KAYIT OL').toUpperCase()}
                </Link>
              </>
            )}
            <UserButton />
            <button className="xl:hidden w-10 h-10 flex items-center justify-center rounded-full border border-white/10 text-white/80" onClick={() => setOpen(!open)}>
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-[#e4b44e]/15 bg-[#0a0a0f]/95 backdrop-blur-xl">
          <div className="px-6 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
            {roots.map((item) => {
              const kids = childrenOf(item.id);
              return (
                <div key={item.id}>
                  <Link to={normUrl(item.url)} className="block py-2 text-sm font-bold tracking-wider text-white/90 uppercase">{trMenu(item.title, normUrl(item.url))}</Link>
                  {kids.length > 0 && (
                    <div className="pl-4 border-l border-[#e4b44e]/20 space-y-1 mb-2">
                      {kids.map((c) => (
                        <Link key={c.id} to={normUrl(c.url)} className="block py-1 text-sm text-white/60 hover:text-[#f4d896]">{trMenu(c.title, normUrl(c.url))}</Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="pt-3 flex flex-wrap gap-2">
              {languagesList.slice(0, 8).map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)}
                  className={cn('h-8 px-3 rounded-full text-[11px] font-bold uppercase transition',
                    lang === l.code ? 'btn-gold' : 'border border-white/12 text-white/70')}>{l.code}</button>
              ))}
            </div>
            {!user && (
              <div className="pt-3 flex gap-2">
                <Link to="/login" className="flex-1 h-11 flex items-center justify-center btn-outline-gold rounded-full text-xs font-bold tracking-wider">{tr('home.login', 'GİRİŞ YAP')}</Link>
                <Link to="/register" className="flex-1 h-11 flex items-center justify-center btn-gold rounded-full text-xs font-extrabold tracking-wider">{tr('home.register', 'KAYIT OL')}</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
